import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import MonthlyHeader from './MonthlyHeader';
import MonthlySummaryBox from './MonthlySummaryBox';
import ExpandableItem from '../../components/MasterComponent/ExpandableItem/ExpandableItem';
import Loading from '../../components/Loading/Loading';

type ServiceItem = {
  name: string;
  rate: string;
  usage: string;
  amount: string;
};

type Region = {
  regionName: string;
  items: ServiceItem[];
};

type ServiceData = {
  serviceName: string;
  total: string;
  regions: Region[];
};

interface MonthlyBillingViewProps {
  servicesData: ServiceData[];
  isLoading: boolean;
  isExpanded: boolean;
  openServices: Record<string, boolean>;
  openRegions: Record<string, Record<string, boolean>>;
  setOpenRegions: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, boolean>>>
  >;
  onToggleAll: () => void;
  onToggleService: (serviceName: string) => void;
}

const MonthlyBillingView: React.FC<MonthlyBillingViewProps> = ({
  servicesData,
  isLoading,
  isExpanded,
  openServices,
  openRegions,
  setOpenRegions,
  onToggleAll,
  onToggleService,
}) => {
  // Check if data is empty or null
  const hasData = Array.isArray(servicesData) && servicesData.length > 0;

  const totalAmount = useMemo(() => {
    if (!hasData) return 0;
    return servicesData.reduce((sum, item) => {
      const numeric = parseFloat(item.total?.replace(/[^0-9.-]+/g, '') || '0');
      return sum + numeric;
    }, 0);
  }, [servicesData]);

  const toggleRegion = (serviceName: string, regionName: string) => {
    setOpenRegions((prev) => ({
      ...prev,
      [serviceName]: {
        ...(prev[serviceName] || {}),
        [regionName]: !prev[serviceName]?.[regionName],
      },
    }));
  };

  const regionTotals = useMemo(() => {
    const totals: Record<string, Record<string, number>> = {};

    if (Array.isArray(servicesData)) {
      servicesData.forEach((service) => {
        if (!totals[service.serviceName]) {
          totals[service.serviceName] = {};
        }

        service.regions?.forEach((region) => {
          const total =
            region.items?.reduce((sum, item) => {
              const num = parseFloat(
                item.amount?.replace(/[^0-9.-]+/g, '') || '0'
              );
              return sum + num;
            }, 0) ?? 0;

          totals[service.serviceName][region.regionName] = total;
        });
      });
    }

    return totals;
  }, [servicesData]);

  return (
    <Box>
      <MonthlyHeader
        isExpanded={isExpanded}
        onToggleAll={onToggleAll}
        disabled={isLoading || !hasData}
      />
      {isLoading ? (
        <Box
          height="60vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      ) : (
        <>
          <MonthlySummaryBox total={`$${totalAmount.toFixed(2)}`} />
          {hasData ? (
            servicesData.map((item) => {
              const isOpen = openServices[item.serviceName] ?? false;

              return (
                <ExpandableItem
                  key={item.serviceName}
                  title={item.serviceName}
                  totalText={item.total}
                  isOpen={isOpen}
                  onToggle={() => onToggleService(item.serviceName)}
                >
                  {item.regions?.map((region) => {
                    const regionTotal =
                      regionTotals?.[item.serviceName]?.[region.regionName] ??
                      0;
                    const isRegionOpen =
                      openRegions[item.serviceName]?.[region.regionName] ??
                      false;

                    return (
                      <ExpandableItem
                        key={region.regionName}
                        title={region.regionName}
                        totalText={`$${regionTotal.toFixed(2)}`}
                        isOpen={isRegionOpen}
                        onToggle={() =>
                          toggleRegion(item.serviceName, region.regionName)
                        }
                      >
                        {region.items?.map((regionItem, idx) => (
                          <Box key={idx} className="region-item">
                            <div className="region-left">
                              <Typography className="region-name">
                                {regionItem.name}
                              </Typography>
                              <Typography className="region-amount">
                                {regionItem.amount}
                              </Typography>
                            </div>
                            <div className="flex-row">
                              <Typography className="region-rate">
                                {regionItem.rate}
                              </Typography>
                              <Typography className="region-amount-rate">
                                {regionItem.amount}
                              </Typography>
                            </div>
                          </Box>
                        ))}
                      </ExpandableItem>
                    );
                  })}
                </ExpandableItem>
              );
            })
          ) : (
            <div style={{ height: '100%' }} className="noDataCenterBox">
              No data available
            </div>
          )}
        </>
      )}
    </Box>
  );
};

export default MonthlyBillingView;
